/**
 * ============================================================================
 * CodeManagementPage — 공통코드 관리 (그룹/상세 분리 편집)
 * ============================================================================
 *
 * 【역할】
 *   시스템 전역 선택박스/Tag/필터에서 사용하는 코드 사전(공통코드)을 관리한다.
 *   좌측 그룹(읽기 전용) — 우측 상세(인라인 편집) 마스터-디테일 패턴.
 *   V24 기준 상세는 locale 별 row 수직(vertical) 정규화 형태로 다국어 지원.
 *
 * 【주요 기능】
 *   - 검색: 그룹코드 자동 조회(첫 진입), 그룹 클릭 시 상세 자동 로드
 *   - CRUD: 상세 행 추가(addRow) / 삭제(deleteRow) / 벌크 저장(commit)
 *   - 인라인 편집: code/locale/value/sortOrder/useYn 컬럼별 Input·Select
 *   - 페이징: 외부 antd Pagination 으로 그룹/상세 각각 분리 슬라이싱
 *   - i18n: useI18nTickStore 로 locale 변경 시 리렌더 트리거
 *
 * 【Backend 호출】
 *   - POST /api/v1/transaction (service=code/searchGroupList)    — 그룹 목록
 *   - POST /api/v1/transaction (service=code/searchListVertical) — 그룹별 상세 (V24 vertical)
 *   - POST /api/v1/transaction (service=code/saveVertical)       — 상세 벌크 저장 (C/U/D)
 *
 * 【DataSet/Hook】
 *   - useDataSet<GroupRow>()  — ds_groupList  : 그룹 목록 (PK: groupCode)
 *   - useDataSet<DetailRow>() — ds_detailList : 상세 목록 (PK: groupCode+code+locale)
 *   - useTransaction()        — RPC 통합 호출/에러/로딩 처리
 *   - useLabel()              — i18n 라벨(t)
 *   - useSupportedLocales()   — locale 드롭다운 옵션
 *
 * 【사용처】 router/index.tsx — `{ path: '/code', element: <CodeManagementPage /> }`
 * 【포팅 원본】 vue-spring-fw `frontend/src/pages/PageCodeManagement.vue`
 * 【의존성】 antd Flex/Input/Pagination/Select/Space/Tag, framework hooks(grid/makeColumns)
 */
import { useEffect, useMemo, useState, useCallback } from 'react'
import { Flex, Input, Pagination, Select, Space, Tag } from 'antd'
import CrudToolbar from '@/components/common/CrudToolbar'
import PageContainer from '@/components/common/PageContainer'
import DataTable from '@/components/common/DataTable'
import { useDataSet, useTransaction } from '@/framework'
import { useLabel } from '@/framework/useLabel'
import { useSupportedLocales } from '@/framework/useLocale'
import { makeColumns, type ColumnSpec } from '@/framework/grid/makeColumns'
import type { TrackedRow } from '@/framework/useDataSet'

/** 좌측 그룹 그리드 1행 — 그룹코드 + 자식 상세 건수. */
interface GroupRow {
  /** 그룹코드 PK (예: "USER_STATUS", "ORDER_TYPE"). */
  groupCode: string
  /** 자식 상세 건수 (집계, 우측 그리드와 일치). */
  codeCount: number
}
/** 우측 상세 그리드 1행 — V24 vertical(locale 별) 정규화 + 구 평면화 호환 필드. */
interface DetailRow {
  /** 소속 그룹코드 (FK → GroupRow.groupCode). */
  groupCode: string
  /** 상세 코드값 PK 일부 (예: "ACTIVE", "PENDING"). */
  code: string
  // V24: vertical 형태 — locale 별 row
  /** 로케일 코드 (예: "ko"·"en"·"zh"·"ja"·"vi"). PK 일부. */
  locale?: string
  /** 로케일 표시명 (서버 join 결과, 예: "한국어"). */
  localeName?: string
  /** 국기 이모지 (서버 join, 예: "🇰🇷"). */
  flagEmoji?: string
  /** 해당 locale 의 코드 표시값 (실제 편집 대상). */
  value?: string
  /** 기본 locale 의 표시명 (회색 텍스트로 표시, 편집 불가). */
  defaultName?: string
  // (구) 평면화 호환 — V23 이전 데이터 호환용
  /** [legacy] 한글명. V24 에서는 locale='ko' 의 value 사용. */
  codeName?: string
  /** [legacy] 영문명. V24 에서는 locale='en' 의 value 사용. */
  codeNameEn?: string
  /** 정렬 순서 (오름차순). */
  sortOrder?: number
  /** 부가 속성 1 (도메인별 자유 사용). */
  attr1?: string
  /** 부가 속성 2. */
  attr2?: string
  /** 부가 속성 3. */
  attr3?: string
  /** 사용여부 ('Y'=사용 / 'N'=미사용). */
  useYn: 'Y' | 'N'
}

/**
 * CodeManagementPage — 컴포넌트/함수 (자동 생성된 JSDoc 스텁).
 * @returns 렌더 결과 또는 반환값
 */
export default function CodeManagementPage() {
  const groupDs = useDataSet<GroupRow>()
  const detailDs = useDataSet<DetailRow>()
  const { transaction } = useTransaction()
  const { t } = useLabel()
  const supportedLocales = useSupportedLocales()
  const localeOptions = useMemo(
    () =>
      supportedLocales.map((l) => ({
        value: l.locale,
        label: `${l.flagEmoji ?? ''} ${l.locale.toUpperCase()} · ${l.nativeName ?? l.name ?? l.locale}`
      })),
    [supportedLocales]
  )
  const [groupCode, setGroupCode] = useState('')

  // 외부 Pagination — DataTable 내부 pagination 대신 Flex column 마지막 자리에 분리 배치
  const GROUP_PAGE_SIZE = 20
  const DETAIL_PAGE_SIZE = 20
  const [groupPage, setGroupPage] = useState(1)
  const [detailPage, setDetailPage] = useState(1)

  /**
   * 그룹 목록 조회 — code/searchGroupList → ds_groupList 갱신.
   * 검색 조건 없이 전체 조회 (그룹 수가 적다고 가정, 페이지 진입 시 1회 + 저장 후 재조회).
   */
  const searchGroup = () =>
    transaction({
      id: 'searchGroup',
      service: 'code/searchGroupList',
      outds: { ds_groupList: groupDs }
    })

  /**
   * 선택 그룹의 상세 조회 — code/searchListVertical → ds_detailList 갱신.
   * @param g 외부에서 전달한 groupCode (생략 시 state 의 groupCode 사용)
   * 미선택 상태(빈 문자열)면 즉시 return (호출 무효화).
   */
  const searchDetail = (g?: string) => {
    const gc = g ?? groupCode
    if (!gc) return
    return transaction({
      id: 'searchDetail',
      // V24: locale 별 row 정규화 (수직 형태)
      service: 'code/searchListVertical',
      inds: { ds_search: { groupCode: gc } },
      outds: { ds_detailList: detailDs }
    })
  }

  /**
   * 좌측 그룹 행 클릭 핸들러.
   * - 선택 groupCode state 갱신 + 디테일 페이지를 1로 reset + 상세 자동 조회.
   * - useCallback 으로 메모이즈하여 자식 onRow 이벤트의 불필요한 재생성 방지.
   */
  const onSelectGroup = useCallback(
    (row: GroupRow) => {
      setGroupCode(row.groupCode)
      setDetailPage(1) // 그룹 전환 시 디테일 페이지 초기화
      searchDetail(row.groupCode)
    },

    []
  )

  /**
   * 상세 행 추가 — 빈 행을 ds_detailList 에 _state='C' 로 push.
   * 그룹 미선택 시 무동작 (그룹 PK 없이는 저장 불가).
   */
  const add = () => {
    if (!groupCode) return
    detailDs.addRow({
      groupCode,
      code: '',
      codeName: '',
      codeNameEn: '',
      sortOrder: 0,
      useYn: 'Y'
    } as DetailRow)
  }

  /** 상세 선택행 삭제 — 신규(C)는 즉시 제거, 기존(U)은 _state='D' 마킹. */
  const del = () => {
    if (detailDs.selectedIndex >= 0) detailDs.deleteRow(detailDs.selectedIndex)
  }

  /**
   * 상세 벌크 저장 — code/saveVertical (C/U/D 변경분만 전송).
   * 성공 시 그룹+상세 모두 재조회 (codeCount 동기화 위해).
   */
  const save = async () => {
    const res = await transaction({
      id: 'save',
      // V24: vertical 저장 (groupCode + code + locale + value)
      service: 'code/saveVertical',
      action: 'save',
      inds: { ds_detailList: detailDs },
      outds: { ds_detailList: detailDs }
    })
    if (res.success) {
      searchGroup()
      searchDetail()
    }
  }

  useEffect(() => {
    // 페이지 진입 → 그룹 조회
    void searchGroup()
  }, [])

  // groupDs 갱신 후 첫 그룹 자동 선택 (vertical detail 자동 표시)
  useEffect(() => {
    if (groupCode) return // 이미 선택된 경우 skip
    const rows = groupDs.getVisibleRows()
    const first = rows[0]?.data as GroupRow | undefined
    if (first?.groupCode) {
      setGroupCode(first.groupCode)
      setDetailPage(1)
      void searchDetail(first.groupCode)
    }
  }, [groupDs.getVisibleRows().length])

  const groupColumns = useMemo(() => {
    const specs: ColumnSpec<TrackedRow<GroupRow>>[] = [
      { key: 'COL_GROUP_CODE', dataIndex: ['data', 'groupCode'], width: '60%' },
      {
        key: 'COL_CODE_COUNT',
        dataIndex: ['data', 'codeCount'],
        width: '40%',
        align: 'center',
        render: (v) => <Tag color="blue">{(v as number | undefined) ?? 0}</Tag>
      }
    ]
    return makeColumns(specs, t)
  }, [t])

  const detailColumns = useMemo(() => {
    type DetailTRow = TrackedRow<DetailRow>
    const specs: ColumnSpec<DetailTRow>[] = [
      {
        key: 'COL_CODE',
        dataIndex: ['data', 'code'],
        width: 120,
        render: (_v, row, idx) => (
          <Input
            size="small"
            value={row.data.code}
            disabled={row._state !== 'C'}
            onChange={(e) => detailDs.updateRow(idx, { code: e.target.value })}
          />
        )
      },
      {
        key: 'COL_LOCALE',
        fallback: '로케일',
        dataIndex: ['data', 'locale'],
        width: 200,
        render: (_v, row, idx) => (
          <Select
            size="small"
            style={{ width: '100%' }}
            value={row.data.locale}
            options={localeOptions}
            onChange={(v) => detailDs.updateRow(idx, { locale: v })}
          />
        )
      },
      {
        key: 'COL_VALUE',
        fallback: '이름',
        dataIndex: ['data', 'value'],
        width: 220,
        render: (_v, row, idx) => (
          <Input
            size="small"
            value={row.data.value}
            onChange={(e) => detailDs.updateRow(idx, { value: e.target.value })}
          />
        )
      },
      {
        key: 'COL_DEFAULT_NAME',
        fallback: '기본 이름',
        dataIndex: ['data', 'defaultName'],
        width: 160,
        render: (_v, row) => <span style={{ color: '#999' }}>{row.data.defaultName}</span>
      },
      {
        key: 'COL_SORT_ORDER',
        dataIndex: ['data', 'sortOrder'],
        width: 80,
        render: (v) => (v as number | undefined) ?? 0
      },
      {
        key: 'COL_USE_YN',
        dataIndex: ['data', 'useYn'],
        width: 90,
        render: (_v, row, idx) => (
          <Select<'Y' | 'N'>
            size="small"
            value={row.data.useYn}
            style={{ width: '100%' }}
            options={[
              { label: t('LBL_YES', '사용'), value: 'Y' },
              { label: t('LBL_NO', '미사용'), value: 'N' }
            ]}
            onChange={(v) => detailDs.updateRow(idx, { useYn: v })}
          />
        )
      },
      {
        key: 'COL_STATUS',
        dataIndex: '_state',
        width: 70,
        render: (s) =>
          s === 'C' ? (
            <Tag color="green">{t('TTL_ADD', '신규')}</Tag>
          ) : s === 'U' ? (
            <Tag color="orange">{t('TTL_EDIT', '수정')}</Tag>
          ) : null
      }
    ]
    return makeColumns(specs, t)
  }, [t, detailDs, localeOptions])

  // 페이지 기반 data slicing (전체 데이터는 DataSet 에 유지, slicing 만 view 에서)
  const allGroup = groupDs.getVisibleRows()
  const allDetail = detailDs.getVisibleRows()
  const pagedGroup = useMemo(
    () => allGroup.slice((groupPage - 1) * GROUP_PAGE_SIZE, groupPage * GROUP_PAGE_SIZE),
    [allGroup, groupPage]
  )
  const pagedDetail = useMemo(
    () => allDetail.slice((detailPage - 1) * DETAIL_PAGE_SIZE, detailPage * DETAIL_PAGE_SIZE),
    [allDetail, detailPage]
  )

  const paneStyle = {
    display: 'flex',
    flexDirection: 'column' as const,
    minHeight: 0,
    gap: 8
  }
  const titleStyle = { margin: 0, fontSize: 14, fontWeight: 600 as const }
  const paginationStyle = { flexShrink: 0 as const, textAlign: 'right' as const, marginTop: 4 }

  return (
    <PageContainer>
      <Flex gap={16} align="stretch" style={{ flex: 1, minHeight: 0 }}>
        {/* 좌측: 그룹코드 — 헤더(제목+조회 버튼) + DataTable + 외부 Pagination
            조회 버튼은 우측 CrudToolbar 와 동일한 디자인 (SearchOutlined 아이콘 + middle size) */}
        <div style={{ ...paneStyle, width: 320, flexShrink: 0 }}>
          <Flex justify="space-between" align="center" style={{ flexShrink: 0 }}>
            <h3 style={titleStyle}>{t('TTL_CODE_GROUP', '그룹코드')}</h3>
            <CrudToolbar onSearch={searchGroup} style={{ width: 'auto' }} />
          </Flex>
          <DataTable<TrackedRow<GroupRow>>
            rowKey="_rowId"
            dataSource={pagedGroup}
            columns={groupColumns}
            onRow={(row) => ({ onClick: () => onSelectGroup(row.data) })}
            rowClassName={(row) =>
              row.data.groupCode === groupCode ? 'ant-table-row-selected' : ''
            }
            autoHeight={false}
            pagination={false}
          />
          <Pagination
            size="small"
            current={groupPage}
            onChange={setGroupPage}
            total={allGroup.length}
            pageSize={GROUP_PAGE_SIZE}
            showSizeChanger={false}
            style={paginationStyle}
          />
        </div>

        {/* 우측: 디테일 — 헤더(그룹태그+CrudToolbar) + DataTable + 외부 Pagination */}
        <div style={{ ...paneStyle, flex: 1, minWidth: 0 }}>
          <Flex justify="space-between" align="center" style={{ flexShrink: 0 }}>
            <div>
              {groupCode ? (
                <Tag>{groupCode}</Tag>
              ) : (
                <span style={{ color: '#888' }}>
                  {t('MSG_SELECT_GROUP_FIRST', '좌측에서 그룹코드를 선택하세요')}
                </span>
              )}
            </div>
            <Space>
              <CrudToolbar
                onSearch={() => searchDetail()}
                onAdd={add}
                onDelete={del}
                onSave={save}
                dirty={detailDs.isDirty}
                style={{ width: 'auto' }}
              />
            </Space>
          </Flex>
          <DataTable<TrackedRow<DetailRow>>
            rowKey="_rowId"
            dataSource={pagedDetail}
            columns={detailColumns}
            rowSelection={{
              type: 'radio',
              selectedRowKeys: detailDs.selectedRow ? [detailDs.selectedRow._rowId] : [],
              onChange: (_k, rows) => {
                const first = rows[0] as TrackedRow<DetailRow> | undefined
                const idx = detailDs.getVisibleRows().findIndex((r) => r._rowId === first?._rowId)
                detailDs.setSelectedIndex(idx)
              }
            }}
            autoHeight={false}
            pagination={false}
          />
          <Pagination
            size="small"
            current={detailPage}
            onChange={setDetailPage}
            total={allDetail.length}
            pageSize={DETAIL_PAGE_SIZE}
            showSizeChanger={false}
            style={paginationStyle}
          />
        </div>
      </Flex>
    </PageContainer>
  )
}
